<script setup lang="ts">
// Globals
import { useSchemaOrg } from "#imports";
import { version } from "~/package.json";

// Types
import type { HotelRoomsResponse } from "~/shared/types/goodbits/hotel";

import { formatDate } from "@citizenm/foundation/utils/date";
import { RATES_INPUT_DATE_FORMAT } from "~/shared/constants";

// Composables
const { params } = useRoute();
const { t, locale } = useI18n();
const localePath = useLocalePath();
const { initCurrency } = useCurrency();
const track = useTracking();
const { trackPageView } = usePageViewTracking(PageTypeTrackingId.webpageCity);

// Stores
const travelDataStore = useTravelDataStore();
const currencyStore = useCurrencyStore();
const snackBarStore = useSnackbarStore();

definePageMeta({
  middleware: "cached-page",
});

await travelDataStore.checkTraveldata();

const { data: cityData, status: hotelsOverviewStatus } =
  await useFetch<CityOverviewResponse>(
    `/api/v2/${params.continent}/${params.city}/overview`,
    {
      query: {
        locale,
      },
      onResponseError({ response }) {
        const { status: statusCode, statusText: message } = response;

        throw showError({
          statusCode,
          message,
        });
      },
    },
  );

if (!cityData.value) {
  throw showError({
    statusCode: 404,
    message: t("website.error.cityNotFound"),
  });
}

const { hotels, marketing, contentfulId } = cityData.value;

if (hotels?.length === 1) {
  // Directly navigate to hotel page if only one hotel is available
  navigateTo(
    localePath({
      name: "hotels-continent-city-hotelSlug",
      params: {
        continent: params.continent,
        city: params.city,
        hotelSlug: hotels[0].fields.slug,
      },
    }),
  );
}

travelDataStore.changeToLocationId(contentfulId);

/* TODO
  The store should have a totalGuests AND guestsPerRoom.
  The totalGuests is the sum of all guests in all rooms.
  This is the safest way to ensure that the API call is correct and
  not to risk harming other areas where totalGuests is used.
*/
const { totalGuests } = useRate();
const apiQueryParams = {
  ...travelDataStore.getDataForQuery,
  totalGuests: totalGuests.value,
  hotelCodes: hotels
    ?.map((hotelEntry) => hotelEntry.fields.hotelMasterContent.fields.code)
    .join(","),
};

if (apiQueryParams.totalGuests! < apiQueryParams.numberOfRooms!) {
  apiQueryParams.totalGuests = apiQueryParams.numberOfRooms;
}

const { data: availabilityResponseData, status: availabilityStatus } =
  await useGoodBitsFetch<HotelRoomsResponse>("/offers/v1/hotel-rooms", {
    query: apiQueryParams,
    lazy: true,
    onResponseError: () => {
      snackBarStore.warning({
        text: t("website.error.loadingHotelAvailability"),
        timeout: false,
        scope: "global",
      });
    },
  });

const hotelsList = computed(() =>
  hotels?.map((hotelEntry) => {
    const { heroImage, hotel } =
      hotelEntry.fields.hotelMasterContent.fields || {};

    const bestPrice = availabilityResponseData.value?.bestPrices.find(
      ({ hotelCode }) =>
        hotelCode === hotelEntry.fields.hotelMasterContent.fields.code,
    );

    // TODO: refactor this hotels map and separate availability/pricing with base hotel data
    // This needs a refactor of HotelListItem as well, which is out of scope for now.
    return {
      // Metadata
      name: hotel.fields.title,
      text: hotel.fields.shortDescription,
      photos: [heroImage.fields],
      roomsCount: 1,
      // Urls
      bookUrl: localePath({
        name: "book",
        query: {
          startdate: formatDate(
            /* @ts-expect-error: travelDataStore casts this value to undefined due to lack of state typing */
            travelDataStore.arrivalDate,
            RATES_INPUT_DATE_FORMAT,
          ),
          enddate: formatDate(
            /* @ts-expect-error: travelDataStore casts this value to undefined due to lack of state typing */
            travelDataStore.departureDate,
            RATES_INPUT_DATE_FORMAT,
          ),
          guestsPerRoom: travelDataStore.guestsPerRoom,
          numberOfRooms: travelDataStore.numberOfRooms,
          hotelCodes: hotelEntry.fields.hotelMasterContent.fields.code,
        },
      }),
      url: localePath({
        name: "hotels-continent-city-hotelSlug",
        params: {
          continent: params.continent,
          city: params.city,
          hotelSlug: hotelEntry.fields.slug,
        },
      }),
      // Price
      pricePerNightIncTax:
        (bestPrice?.totalAmount.totalGrossAmount || 0) /
        travelDataStore.getNightsOfStay,
      pricePerNightExcTax:
        (bestPrice?.totalAmount.totalGrossAmountExcl || 0) /
        travelDataStore.getNightsOfStay,
      pricePerStayIncTax: bestPrice?.totalAmount.totalGrossAmount,
      pricePerStayExcTax: bestPrice?.totalAmount.totalGrossAmountExcl,
      currency: bestPrice?.totalAmount?.currency,
      // Availability
      roomsAvailable:
        availabilityResponseData.value?.availability.availableHotels.includes(
          hotelEntry.fields.hotelMasterContent.fields.code,
        )
          ? 1
          : 0,
      loaded: ["success", "error"].includes(availabilityStatus.value),
    };
  }),
);

const hotelCount = computed(() => hotelsList.value?.length || 0);
const viewReady = computed(() => hotelsOverviewStatus.value === "success");

if (import.meta.client) {
  initCurrency();

  trackPageView({
    title: marketing?.metadata?.seoTitle,
    ecommerceStage: "property-list",
  });

  track.trackPageInfo({
    pageType: "webpageCity",
    pageTitle: marketing?.metadata?.seoTitle,
    ecommerceStage: "property-list",
    version,
  });
}

const {
  metadata: { seoDescription: description, seoTitle: title } = {},
  fields: {
    location: {
      fields: {
        title: name,
        coordinates: { lat: latitude, lon: longitude } = {},
      } = {},
    } = {},
  } = {},
} = marketing || {};

const containsPlace = computed(() => {
  return (hotels || []).map((hotel) => {
    const { heroImage, location, title } =
      hotel?.fields?.hotelMasterContent?.fields;
    const { coordinates } = location?.fields;

    return {
      "@type": "Hotel",
      name: `citizenM ${title}`,
      image: heroImage?.fields?.asset?.[0].thumbnails.webimage,
      geo: {
        "@type": "GeoCoordinates",
        latitude: coordinates?.lat,
        longitude: coordinates?.lon,
      },
    };
  });
});

useSeoMeta({
  description,
  title,
  ogTitle: title,
  ogDescription: description,
});

// i18n settings
const setI18nParams = useSetI18nParams();
const localizedRouteParams = await useLocalizedRouteParams();
if (localizedRouteParams.value) {
  setI18nParams(localizedRouteParams.value);
}

// Schema.org structured data
useSchemaOrg([
  {
    "@type": "City",
    name,
    description,
    geo: {
      "@type": "GeoCoordinates",
      latitude,
      longitude,
    },
    containsPlace,
  },
]);
</script>

<template>
  <div
    v-if="viewReady"
    class="main-app continent-view"
    role="main"
    aria-labelledby="main-title"
  >
    <div class="wrap">
      <HotelSearchBar bar has-shadow :submit-url="localePath('/book')" />
    </div>
    <div class="wrap">
      <H level="1" size="5" class="mb16" id="main-title">
        {{
          $t("website.general.location.xHotelsInLocation", {
            count: hotelCount,
            location: name,
          })
        }}
      </H>
      <div class="actions-row">
        <CurrencyChangeSelect
          :label="$t('website.general.currency')"
          :labelLocalCurrency="$t('website.general.localCurrency')"
          :has-border="false"
        />
        <PriceTypeChange
          :label="$t('website.general.price')"
          @change="travelDataStore.selectPriceType"
          :selected="travelDataStore.selectedPriceType"
        />
        <!-- WMD1-1818
          <ButtonMap
              @click.native="toggleMap"
              variant="white"
              size="small"
              class="map-trigger"
          >
            {{ showMap ? $t("hideMap") : $t("showMap") }}
          </ButtonMap>
          -->
      </div>

      <!-- WMD1-1818
        <HotelMapView v-if="showMap" :hotels="hotels"/>
        -->
      <HotelList
        :selected-price-type="travelDataStore.selectedPriceType"
        :hotels="hotelsList"
        :selected-currency="currencyStore.selectedCurrency"
      />
    </div>
    <div class="wrap explore" v-if="cityData?.marketing?.fields?.content">
      <ContentfulSection
        v-for="c in cityData?.marketing?.fields?.content"
        :content="c"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.explore,
.faqs {
  margin-top: var(--space-56);
}

section {
  margin-bottom: 24px;
}
</style>
